import api from "./api";
import {
  ok, getMe, can, loadScoringConfig, toBands, toWeights, isoUtc, nameOf, getLookupsCached, cycleById, ratingConfigFor,
  publicConfig, stripExtensionTag,
} from "./core";
import { baseCode, pipBandsOfScheme, pipRuleFor } from "../utils/ratingScale";
import { BACKEND } from "../config/backendFeatures";

const latestBy = (rows, key) => [...(rows || [])].sort((a, b) => (b.version_no - a.version_no) || (a[key] < b[key] ? 1 : -1));

export async function getRatingSettings() {
  const { policies, schemes, restricted } = await loadScoringConfig(true);
  if (restricted) {
    const defaults = { weights: toWeights(null), bands: toBands([]) };
    return ok({ ...defaults, updatedByName: "—", updatedAt: null, readOnly: true, policies: [], schemes: [] });
  }
  const approvedPolicy = latestBy(policies.filter((p) => p.policy_status === "APPROVED"), "created_at")[0];
  const approvedScheme = latestBy(schemes.filter((s) => s.scheme_status === "APPROVED"), "created_at")[0];
  const policy = approvedPolicy || latestBy(policies, "created_at")[0] || null;
  const scheme = approvedScheme || latestBy(schemes, "created_at")[0] || null;
  const newest = [policy, scheme].filter(Boolean).sort((a, b) => ((a.approved_at || a.created_at) < (b.approved_at || b.created_at) ? 1 : -1))[0];
  return ok({
    weights: toWeights(policy),
    bands: toBands(scheme?.bands || []),
    pipBands: pipBandsOfScheme(scheme), // PM-9: automatic PIP for the lowest N bands (0 = off)
    policy,
    scheme,
    updatedByName: newest ? nameOf(newest.approved_by_employee_id || newest.created_by_employee_id) : "—",
    updatedAt: newest ? isoUtc(newest.approved_at || newest.created_at) : null,
    policies: latestBy(policies, "created_at").map((p) => ({
      kind: "scoring_policy", id: p.scoring_policy_id, name: `${p.policy_code} v${p.version_no}`, status: p.policy_status,
      detail: `Goals ${Number(p.goal_weight)}% / KRA ${Number(p.kra_weight)}% / KPI ${Number(p.kpi_weight)}%`,
      createdAt: isoUtc(p.created_at), createdBy: nameOf(p.created_by_employee_id),
    })),
    schemes: latestBy(schemes, "created_at").map((s) => ({
      kind: "rating_scheme", id: s.rating_scheme_id, name: `${s.scheme_code} v${s.version_no}`, status: s.scheme_status,
      detail: (s.bands || []).map((b) => b.rating_label).join(", "),
      createdAt: isoUtc(s.created_at), createdBy: nameOf(s.created_by_employee_id),
    })),
    readOnly: false,
  });
}

// [UPDATED] No scale choice any more (Scoring & ratings is the original page). PM-9: the automatic PIP rule is kept:
// pipBands = lowest N bands that start a PIP draft (0 = off); when not given, the current rule is kept.
export async function updateRatingSettings({ weights, bands, pipBands }) {
  const me = await getMe();
  const { policies, schemes } = await loadScoringConfig(true);
  const base = latestBy(policies || [], "created_at")[0];
  const policyCode = base?.policy_code || "STANDARD";
  const policyVersion = Math.max(0, ...(policies || []).filter((p) => p.policy_code === policyCode).map((p) => p.version_no)) + 1;
  const sorted = [...bands].map((b) => ({ ...b, min: Number(b.min), max: Number(b.max) })).sort((a, b) => a.min - b.min);
  if (!sorted.length || sorted[0].min !== 0) throw new Error("The lowest band must start at 0.");
  const apiBands = sorted.map((b, i) => ({
    rating_code: String(b.code || b.label).toUpperCase().replace(/[^A-Z0-9]+/g, "_").slice(0, 50) || `BAND_${i + 1}`,
    rating_label: b.label,
    min_score: b.min,
    max_score: i === sorted.length - 1 ? null : sorted[i + 1].min,
  }));
  const schemeBase = latestBy(schemes || [], "created_at")[0];
  const rule = pipBands === undefined || pipBands === null ? pipBandsOfScheme(schemeBase) : Number(pipBands) || 0;
  const plain = baseCode(schemeBase?.scheme_code || "STANDARD");
  // [UPDATED] Requirement 6: the rule always rides in the scheme code (STANDARD_PIP1) and the backend pip_rule is saved
  // as switched off, so the backend never starts a plan by itself; the frontend creates the PIP as a DRAFT.
  const schemeCode = rule ? `${plain}_PIP${rule}` : plain;
  const schemeVersion = Math.max(0, ...(schemes || []).filter((s) => s.scheme_code === schemeCode).map((s) => s.version_no)) + 1;

  const { data: policy } = await api.post("/configuration", {
    kind: "scoring_policy",
    data: {
      policy_code: policyCode,
      version_no: policyVersion,
      goal_weight: Number(weights.goals),
      kra_weight: Number(weights.kra),
      kpi_weight: Number(weights.kpi),
      score_cap: Number(base?.score_cap || 100),
      rounding_places: base?.rounding_places ?? 2,
      rounding_mode: base?.rounding_mode || "HALF_UP",
      missing_component_rule: base?.missing_component_rule || "RENORMALIZE_AVAILABLE",
      goal_formula: "ACHIEVEMENT_PERCENT",
      kra_formula: "DIRECTION_RATIO",
      kpi_formula: "DIRECTION_RATIO",
      reason: "Scoring weights updated",
    },
  });
  const { data: scheme } = await api.post("/configuration", {
    kind: "rating_scheme",
    data: {
      scheme_code: schemeCode, version_no: schemeVersion, bands: apiBands, reason: "Rating bands updated",
      // [NEW] PM-9: backend field from migration 016 (REACT_APP_BACKEND_RATING_SCALE=true).
      ...(BACKEND.ratingScaleField ? { pip_rule: pipRuleFor(0) } : {}),
    },
  });
  let approved = false;
  if (can(me, "policy.approve")) {
    await api.post(`/configuration/scoring_policy/${policy.scoring_policy_id}/approve`, { reason: "Approved from Scoring & ratings" });
    await api.post(`/configuration/rating_scheme/${scheme.rating_scheme_id}/approve`, { reason: "Approved from Scoring & ratings" });
    approved = true;
  }
  await loadScoringConfig(true);
  return ok({ policy, scheme, approved });
}

export const listConfigurations = (kind) => api.get(`/configuration/${kind}`);

export async function getCycleRatingConfig(cycleId) {
  const config = await ratingConfigFor(await cycleById(cycleId));
  return ok(publicConfig(config));
}

export async function approveConfiguration(kind, id, reason = "Approved") {
  const res = await api.post(`/configuration/${kind}/${id}/approve`, { reason });
  await loadScoringConfig(true);
  return res;
}

export async function getMasters(kind) {
  const { data } = await api.get(`/masters/${kind}`);
  return ok(data.map((m) => ({ id: m[`${kind}_id`], code: m[`${kind}_code`], name: m[`${kind}_name`], active: Boolean(m.is_active) })));
}

export async function saveMaster(kind, { id, code, name, active = true, reason }) {
  const body = { code: String(code || "").trim(), name: String(name || "").trim(), is_active: active, reason: reason || `${kind} saved` };
  const res = id ? await api.put(`/masters/${kind}/${id}`, body) : await api.post(`/masters/${kind}`, body);
  await getLookupsCached(true);
  return res;
}

export async function getCompanySettings() {
  const { data } = await api.get("/company-settings");
  return ok(data ? {
    companyName: data.company_name,
    timezone: data.business_timezone,
    financialYearStartMonth: data.financial_year_start_month,
    locale: data.default_locale,
    retentionPolicy: data.retention_policy || {},
  } : null);
}

export const updateCompanySettings = (form) => api.put("/company-settings", {
  company_name: form.companyName,
  business_timezone: form.timezone,
  financial_year_start_month: Number(form.financialYearStartMonth),
  default_locale: form.locale,
  retention_policy: form.retentionPolicy || {},
  reason: form.reason || "Company settings updated",
});

export const AUDIT_RESOURCE_TYPES = [
  { value: "PERFORMANCE_RECORD", label: "Performance record" },
  { value: "PERFORMANCE_ITEM", label: "Goal / KRA / KPI" },
  { value: "GOAL_CHANGE_REQUEST", label: "Goal change request" },
  { value: "PERFORMANCE_CYCLE", label: "Performance cycle" },
  { value: "EMPLOYEE", label: "Employee" },
  { value: "LOCAL_ACCOUNT", label: "Login account" },
  { value: "ROLE", label: "Role" },
  { value: "USER_ROLE", label: "Role assignment" },
  { value: "SCORING_POLICY", label: "Scoring policy" },
  { value: "RATING_SCHEME", label: "Rating scheme" },
  { value: "PERFORMANCE_TEMPLATE", label: "Role template" },
  { value: "PIP_PLAN", label: "Improvement plan" },
  { value: "REPORT_EXPORT", label: "Report export" },
  { value: "AUDIT_LOG", label: "Audit search" },
  { value: "HTTP_REQUEST", label: "Rejected request" },
];

const pretty = (v) => String(v || "").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

export async function getAuditTrail(params = {}) {
  const { data } = await api.get("/audit-logs", {
    params: {
      limit: params.limit || 200,
      offset: params.offset || 0,
      resource_type: params.entityType || undefined,
      outcome: params.outcome || undefined,
      actor_employee_id: params.actorId || undefined,
      since: params.since || undefined,
      until: params.until || undefined,
    },
  });
  const typeLabel = Object.fromEntries(AUDIT_RESOURCE_TYPES.map((t) => [t.value, t.label]));
  const rows = data.items.map((r) => ({
    id: r.activity_id,
    at: isoUtc(r.created_at),
    entityType: typeLabel[r.resource_type] || pretty(r.resource_type),
    resourceType: r.resource_type, // [NEW] PM-18: raw code for the multi-select filter
    entityId: r.resource_id,
    action: pretty(r.event_type),
    outcome: r.outcome,
    employeeName: r.employee_id ? nameOf(r.employee_id) : (r.resource_type === "EMPLOYEE" || r.resource_type === "LOCAL_ACCOUNT") ? nameOf(r.resource_id) : r.resource_id || "—",
    actorName: nameOf(r.actor_employee_id, r.actor_subject || "System"),
    comment: [stripExtensionTag(r.reason), r.error_code && `Error: ${r.error_code}`].filter(Boolean).join(" · "),
    changes: auditChanges(r.before_data, r.after_data),
    requestId: r.request_id,
  }));
  const q = (params.search || "").trim().toLowerCase();
  return ok(q ? filterAudit(rows, q) : rows);
}

export function filterAudit(rows = [], search = "") {
  const q = String(search || "").trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => [r.action, r.actorName, r.employeeName, r.comment, r.entityType, r.changes, r.entityId].some((v) => String(v || "").toLowerCase().includes(q)));
}

const parseJson = (v) => {
  if (!v) return null;
  if (typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return null; }
};
const SKIP_KEYS = new Set(["updated_at", "created_at", "version_no", "row_version", "password_hash"]);
const short = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const text = typeof v === "object" ? JSON.stringify(v) : String(v);
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
};
export function auditChanges(beforeRaw, afterRaw) {
  const before = parseJson(beforeRaw);
  const after = parseJson(afterRaw);
  if (!before || !after || typeof before !== "object" || typeof after !== "object") return "";
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter((k) => !SKIP_KEYS.has(k));
  return keys
    .filter((k) => k in after && JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    .slice(0, 8)
    .map((k) => `${k.replace(/_/g, " ")}: ${short(before[k])} → ${short(after[k])}`)
    .join("; ");
}

